import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import ProviderProfileForm from "./ProviderProfileForm";
import { checkPermissionByKey } from "../utils/checkPermissionByKey";
import { useSelector } from "react-redux";
import {
  FaEdit,
  FaArrowLeft,
  FaUserMinus,
  FaUserPlus,
  FaTimes,
} from "react-icons/fa";
import StepProfilePhoto from "./ProviderRegistration/StepProfilePhoto";
import ProviderProfileTab from "./ProviderProfileTab";
import ProviderCredentialsTab from "./ProviderCredentialsTab";
import ProviderPaymentsTab from "./ProviderPaymentsTab";
import ProviderAvailabilityTab from "./ProviderAvailabilityTab";
import ProviderSettingsTab from "./ProviderSettingsTab";
import "./ProviderDetails.css";

const STATUS_COLORS = {
  Active: { bg: "#D1FAE5", color: "#047857" },
  Pending: { bg: "#FFEDD5", color: "#C2410C" },
  Inactive: { bg: "#FEE2E2", color: "#DC2626" },
};

const appendProviderFormField = (formData, key, value) => {
  if (value === null || value === undefined) return;
  if (typeof value === "object") {
    formData.append(key, JSON.stringify(value));
    return;
  }
  formData.append(key, String(value));
};

const appendProviderFieldsToFormData = (formData, provider, skipKeys = []) => {
  Object.keys(provider).forEach((key) => {
    if (skipKeys.includes(key)) return;
    appendProviderFormField(formData, key, provider[key]);
  });
};

const toDisplayStatus = (rawStatus) => {
  const status = String(rawStatus || "deactivated").toLowerCase();
  if (status === "active") return "Active";
  if (status === "deactivated" || status === "inactive") return "Inactive";
  return "Pending";
};

const StatusPill = ({ status }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  return (
    <span
      className="pp-status-pill"
      style={{ backgroundColor: colors.bg, color: colors.color }}
    >
      {status}
    </span>
  );
};

const ProfileSection = ({ title, subtitle, onEdit, editLabel = "Edit", extraActions, children }) => (
  <div className="pp-section">
    <div className="pp-section-header">
      <h3>{title}</h3>
      <div className="pp-section-header-actions">
        {extraActions}
        {onEdit && (
          <button type="button" className="pp-section-edit" onClick={onEdit}>
            {editLabel}
          </button>
        )}
      </div>
    </div>
    <div className="pp-section-body">
      {subtitle && <p className="pp-section-subtitle">{subtitle}</p>}
      {children}
    </div>
  </div>
);

const ProviderDetails = ({ location: locationProp, isOwnProfile = false }) => {
  const routerLocation = useLocation();
  const location = locationProp || routerLocation;
  const { provider: initialProvider } = location?.state || {};
  const [showModal, setShowModal] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [provider, setProvider] = useState(initialProvider);
  const [updatedProvider, setUpdatedProvider] = useState(initialProvider);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const userSlice = useSelector((state) => state?.user);
  const user = userSlice?.user;

  // Editing states
  const [editingPhoto, setEditingPhoto] = useState(false);
  const [photoLoadFailed, setPhotoLoadFailed] = useState(false);
  const [editingPersonalInfo, setEditingPersonalInfo] = useState(false);
  const [editingSpecializations, setEditingSpecializations] = useState(false);
  const [editingVideo, setEditingVideo] = useState(false);
  const [tempPersonalInfo, setTempPersonalInfo] = useState({});
  const [tempSpecializations, setTempSpecializations] = useState([]);
  const [tempPhoto, setTempPhoto] = useState(null);
  const [tempVideo, setTempVideo] = useState(null);

  // Credentials tab states
  const [fetchedStates, setFetchedStates] = useState([]);

  // Tab state — support ?tab= deep links from onboarding checklist
  const searchParams = new URLSearchParams(location.search);
  const tabFromQuery = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromQuery || "profile");

  useEffect(() => {
    if (tabFromQuery) {
      setActiveTab(tabFromQuery);
    }
  }, [tabFromQuery]);

  useEffect(() => {
    const fetchProviderData = async () => {
      if (id) {
        try {
          setIsLoading(true);
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/specialist/provider/${id}`
          );
          if (response.data.success) {
            setProvider(response.data.data);
            setUpdatedProvider(response.data.data);
          } else if (initialProvider) {
            setProvider(initialProvider);
            setUpdatedProvider(initialProvider);
          } else {
            toast.error('Failed to fetch provider data');
          }
        } catch (error) {
          console.error('Error fetching provider:', error);
          if (initialProvider) {
            setProvider(initialProvider);
            setUpdatedProvider(initialProvider);
          } else {
            toast.error('Error fetching provider data');
          }
        } finally {
          setIsLoading(false);
        }
      } else if (initialProvider) {
        setProvider(initialProvider);
        setUpdatedProvider(initialProvider);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };

    fetchProviderData();
  }, [id, initialProvider]);

  useEffect(() => {
    setPhotoLoadFailed(false);
  }, [provider?.profilePhotoUrl]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/states/all`
        );
        if (response.data.success) {
          const sorted = response.data.data.sort((a, b) =>
            (a.name || "").localeCompare(b.name || "")
          );
          setFetchedStates(sorted);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };

    fetchStates();
  }, []);

  const providerType = provider?.providerType || "Therapist";
  const isCareManager = providerType === "Care Manager";
  const isPsychiatricConsultant = providerType === "Psychiatric Consultant";

  const PROFILE_TABS = useMemo(() => {
    let tabs;
    if (isCareManager) {
      tabs = [
        { id: "profile", label: "Profile" },
        { id: "availability", label: "Availability" },
        { id: "settings", label: "Settings" },
      ];
    } else if (isPsychiatricConsultant) {
      tabs = [
        { id: "profile", label: "Profile" },
        { id: "settings", label: "Settings" },
      ];
    } else {
      tabs = [
        { id: "profile", label: "Profile" },
        { id: "credentials", label: "Licenses & Credentials" },
        { id: "payments", label: "Payments Accepted" },
        { id: "availability", label: "Availability" },
        { id: "settings", label: "Settings" },
      ];
    }

    // Therapist panel: Availability & Settings live in the sidebar nav.
    if (isOwnProfile) {
      tabs = tabs.filter(
        (tab) => tab.id !== "availability" && tab.id !== "settings",
      );
    }

    return tabs;
  }, [isCareManager, isPsychiatricConsultant, isOwnProfile]);

  useEffect(() => {
    const allowed = new Set(PROFILE_TABS.map((tab) => tab.id));
    if (!allowed.has(activeTab)) {
      setActiveTab("profile");
    }
  }, [PROFILE_TABS, activeTab]);

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-text">Loading provider details...</div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="error-container">
        <div className="error-text">No Provider data available</div>
      </div>
    );
  }

  const getProviderId = () => {
    if (isOwnProfile && provider?._id) {
      return provider._id;
    }
    return id;
  };

  // Validation function
  const validatePersonalInfo = (info) => {
    const errors = [];
    if (!info.firstname?.trim()) errors.push("First Name is required");
    if (!info.lastname?.trim()) errors.push("Last Name is required");
    if (!info.displayName?.trim()) errors.push("Display Name is required");
    if (!info.providerType?.trim()) errors.push("Role is required");
    const isTherapistType =
      !info.providerType || info.providerType === "Therapist";
    if (isTherapistType) {
      if (
        info.yearsOfExperience === "" ||
        info.yearsOfExperience === null ||
        info.yearsOfExperience === undefined ||
        Number(info.yearsOfExperience) < 0
      ) {
        errors.push("Years of Experience is required");
      }
    }
    return errors;
  };

  const handleDeactivate = () => {
    setShowModal(true);
  };

  const handleActivate = async () => {
    setIsDeactivating(true);
    try {
      const request = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/specialist/activateprovider/${id || getProviderId()}`
      );
      if (request.data.success) {
        toast.success(request.data.message || "Provider activated successfully.");
        setProvider((prev) => ({ ...prev, status: "Active" }));
        setUpdatedProvider((prev) => ({ ...prev, status: "Active" }));
      } else {
        toast.error('Could not activate user.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not activate provider.");
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedProvider({ ...updatedProvider, [name]: value });
  };

  const handleConfirmDeactivate = async () => {
    setIsDeactivating(true);
    try {
      const currentStatus = String(provider?.status || "").trim();
      const statusForApi = (() => {
        if (
          ["Active", "Deactivated", "Invited", "Incomplete"].includes(
            currentStatus,
          )
        ) {
          return currentStatus;
        }
        const lower = currentStatus.toLowerCase();
        if (lower === "active") return "Active";
        if (lower === "deactivated" || lower === "inactive") {
          return "Deactivated";
        }
        if (lower === "invited") return "Invited";
        if (lower === "incomplete") return "Incomplete";
        // Pending / unknown: treat as Active so deactivate can proceed
        return "Active";
      })();

      const request = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/specialist/deactivateprovider/${id || getProviderId()}`,
        { status: statusForApi },
      );
      if (request.data.success) {
        toast.success(request.data.message || "Provider deactivated successfully.");
        setProvider((prev) => ({ ...prev, status: "Deactivated" }));
        setUpdatedProvider((prev) => ({ ...prev, status: "Deactivated" }));
      } else {
        toast.error('Could not deactivate user.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not deactivate provider.");
    } finally {
      setIsDeactivating(false);
      setShowModal(false);
    }
  };

  const handlePhotoEdit = () => {
    setEditingPhoto(true);
    setHasUnsavedChanges(true);
  };

  const handlePhotoSave = async (photoFile) => {
    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('profilePhoto', photoFile);

      // Send all existing provider data to preserve unchanged fields
      appendProviderFieldsToFormData(formData, provider, [
        "profilePhoto",
        "profilePhotoUrl",
      ]);

      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (request.data.success) {
        setProvider({ ...provider, profilePhotoUrl: request.data.data.profilePhotoUrl });
        setUpdatedProvider({ ...updatedProvider, profilePhotoUrl: request.data.data.profilePhotoUrl });
        toast.success('Your profile has been updated.');
        setEditingPhoto(false);
        setTempPhoto(null);
        setHasUnsavedChanges(false);
      } else {
        toast.error(request.data.message || 'Failed to update profile photo');
      }
    } catch (error) {
      if (!error.response) {
        toast.error("Network connection lost. Please check your connection and try again.");
      } else {
        toast.error(error.response.data.message || "An unexpected error occurred.");
      }
      console.error('Photo update error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoCancel = () => {
    setEditingPhoto(false);
    setTempPhoto(null);
    setHasUnsavedChanges(false);
  };

  // Personal info editing handlers
  const handlePersonalInfoEdit = () => {
    setTempPersonalInfo({
      firstname: provider.firstname || "",
      lastname: provider.lastname || "",
      displayName:
        provider.displayName ||
        `${provider.firstname || ""} ${provider.lastname || ""}`.trim(),
      yearsOfExperience:
        provider.yearsOfExperience !== undefined &&
        provider.yearsOfExperience !== null
          ? provider.yearsOfExperience
          : "",
      providerType: provider.providerType || "Therapist",
    });
    setEditingPersonalInfo(true);
    setHasUnsavedChanges(true);
  };

  const handlePersonalInfoChange = (field, value) => {
    setTempPersonalInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);
  };

  const handlePersonalInfoSave = async () => {
    const validationErrors = validatePersonalInfo(tempPersonalInfo);

    if (validationErrors.length > 0) {
      toast.error(
        `Please complete all required fields: ${validationErrors.join(", ")}`,
      );
      return;
    }

    try {
      setIsSaving(true);

      const personalInfoUpdate = {
        firstname: tempPersonalInfo.firstname.trim(),
        lastname: tempPersonalInfo.lastname.trim(),
        displayName: tempPersonalInfo.displayName.trim(),
        providerType: tempPersonalInfo.providerType,
        yearsOfExperience:
          !tempPersonalInfo.providerType ||
          tempPersonalInfo.providerType === "Therapist"
            ? Number(tempPersonalInfo.yearsOfExperience)
            : null,
      };

      if (personalInfoUpdate.providerType !== "Therapist") {
        personalInfoUpdate.gender = null;
        personalInfoUpdate.specialisation = null;
        personalInfoUpdate.fees = null;
      }

      const updateData = {
        ...provider,
        ...personalInfoUpdate,
      };

      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
        updateData,
      );

      if (request.data.success) {
        setProvider({
          ...provider,
          ...personalInfoUpdate,
        });

        setUpdatedProvider({
          ...updatedProvider,
          ...personalInfoUpdate,
        });

        toast.success("Personal info updated successfully.");
        setEditingPersonalInfo(false);
        setTempPersonalInfo({});
        setHasUnsavedChanges(false);
      } else {
        toast.error(
          request.data.message || "Failed to update personal information",
        );
      }
    } catch (error) {
      if (!error.response) {
        toast.error(
          "Network connection lost. Please check your connection and try again.",
        );
      } else {
        toast.error(
          error.response.data.message || "An unexpected error occurred.",
        );
      }

      console.error("Personal info update error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePersonalInfoCancel = () => {
    setEditingPersonalInfo(false);
    setTempPersonalInfo({});
    setHasUnsavedChanges(false);
  };

  // Specializations editing handlers
  const handleSpecializationsEdit = () => {
    let currentSpecializations = [];
    if (Array.isArray(provider.specialisation)) {
      currentSpecializations = provider.specialisation.filter(
        (s) => s && !String(s).startsWith("__TOP__="),
      );
    } else if (provider.specialisation) {
      currentSpecializations = String(provider.specialisation)
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s && !s.startsWith("__TOP__="));
    }
    setTempSpecializations(currentSpecializations);
    setEditingSpecializations(true);
    setHasUnsavedChanges(true);
  };

  const handleSpecializationsSave = async (payload) => {
    const selected =
      payload?.specializations ||
      (Array.isArray(tempSpecializations) ? tempSpecializations : []);

    if (selected.length === 0) {
      toast.error("Please select at least one specialization");
      return;
    }

    try {
      setIsSaving(true);
      const specialisationString = selected.join(",");

      const updateData = {
        ...provider,
        specialisation: specialisationString,
      };

      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
        updateData,
      );

      if (request.data.success) {
        setProvider({ ...provider, specialisation: specialisationString });
        setUpdatedProvider({
          ...updatedProvider,
          specialisation: specialisationString,
        });
        toast.success("Specialties updated successfully.");
        setEditingSpecializations(false);
        setTempSpecializations([]);
        setHasUnsavedChanges(false);
      } else {
        toast.error(
          request.data.message || "Failed to update specializations",
        );
      }
    } catch (error) {
      if (!error.response) {
        toast.error(
          "Network connection lost. Please check your connection and try again.",
        );
      } else {
        toast.error(
          error.response.data.message || "An unexpected error occurred.",
        );
      }
      console.error("Specializations update error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSpecializationsCancel = () => {
    setEditingSpecializations(false);
    setTempSpecializations([]);
    setHasUnsavedChanges(false);
  };

  // Video editing handlers
  const handleVideoEdit = () => {
    setEditingVideo(true);
    setHasUnsavedChanges(true);
  };

  const handleVideoSave = async (videoFile) => {
    try {
      setIsSaving(true);
      console.log('Video upload - Starting upload process');
      console.log('Video upload - File received:', videoFile);

      if (!videoFile) {
        toast.error('No video file selected');
        return;
      }

      const formData = new FormData();
      formData.append('videoIntro', videoFile);

      // Send all existing provider data to preserve unchanged fields
      appendProviderFieldsToFormData(formData, provider, [
        "videoIntro",
        "videoIntroUrl",
      ]);

      console.log('Video upload - Provider data being sent to preserve fields');

      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      console.log('Video upload - Response received:', request.data);

      if (request.data.success) {
        setProvider({ ...provider, videoIntroUrl: request.data.data.videoIntroUrl });
        setUpdatedProvider({ ...updatedProvider, videoIntroUrl: request.data.data.videoIntroUrl });
        toast.success('Your profile has been updated.');
        setEditingVideo(false);
        setTempVideo(null);
        setHasUnsavedChanges(false);
      } else {
        toast.error(request.data.message || 'Failed to update video intro');
      }
    } catch (error) {
      console.error('Video update error details:', error.response?.data);
      if (!error.response) {
        toast.error("Network connection lost. Please check your connection and try again.");
      } else {
        toast.error(error.response.data.message || "An unexpected error occurred.");
      }
      console.error('Video update error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleVideoCancel = () => {
    setEditingVideo(false);
    setTempVideo(null);
    setHasUnsavedChanges(false);
  };

  const displayStatus = toDisplayStatus(provider.status);
  const isTherapist =
    !provider.providerType || provider.providerType === "Therapist";
  const providerDisplayId =
    provider.providerId ||
    (provider._id ? `NUM-${String(provider._id).slice(-6).toUpperCase()}` : "—");
  const displayName =
    provider.displayName ||
    `${provider.firstname || ""} ${provider.lastname || ""}`.trim() ||
    "—";
  const photoInitials = (() => {
    const first = String(provider.firstname || "").trim()[0] || "";
    const last = String(provider.lastname || "").trim()[0] || "";
    const fromParts = `${first}${last}`.toUpperCase();
    if (fromParts) return fromParts;
    const nameParts = String(displayName === "—" ? "" : displayName)
      .replace(/^(dr|mr|mrs|ms|prof)\.?\s+/i, "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!nameParts.length) return "?";
    if (nameParts.length === 1) return nameParts[0].slice(0, 2).toUpperCase();
    return `${nameParts[0][0] || ""}${nameParts[1][0] || ""}`.toUpperCase();
  })();
  const specialtyParts = provider.specialisation
    ? String(provider.specialisation)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const specialtyMeta = specialtyParts.find((item) =>
    item.startsWith("__TOP__="),
  );
  const specialtyList = specialtyParts.filter(
    (item) => !item.startsWith("__TOP__="),
  );
  const topCount = specialtyMeta
    ? Math.min(3, Math.max(0, Number(specialtyMeta.split("=")[1]) || 0))
    : Math.min(3, specialtyList.length);
  const topSpecialties = specialtyList.slice(0, topCount);
  const otherSpecialties = specialtyList.slice(topCount);
  const languages = Array.isArray(provider.languages)
    ? provider.languages
    : provider.languages
      ? String(provider.languages).split(",").map((s) => s.trim()).filter(Boolean)
      : [];
  const populations = Array.isArray(provider.populationsServed)
    ? provider.populationsServed
    : provider.populationsServed
      ? String(provider.populationsServed).split(",").map((s) => s.trim()).filter(Boolean)
      : [];
  const raceEthnicity = provider.raceEthnicity || provider.race || "";
  const genderIdentity = provider.genderIdentity || provider.gender || "";
  const phone = provider.phone || provider.phoneNumber || "";
  const bio = provider.bio || provider.shortBio || "";
  const canManageStatus =
    !isOwnProfile &&
    (checkPermissionByKey(
      userSlice,
      "providerManagement",
      "ActivateDeactivateProvider",
    ) ||
      checkPermissionByKey(userSlice, "providerManagement", "DeactivateProvider") ||
      checkPermissionByKey(userSlice, "providerManagement", "ActivateProvider") ||
      user?.role === "superAdmin" ||
      user?.role === "admin");

  return (
    <>
      {!isEditing ? (
        <div className="app-page provider-profile-shell">
          {!isOwnProfile && (
            <div className="pp-page-top">
              <button
                type="button"
                className="pp-back-btn"
                onClick={() => navigate("/providers")}
                aria-label="Back to providers"
              >
                <FaArrowLeft size={12} />
              </button>
              <h1 className="pp-page-title">Provider Profile</h1>
            </div>
          )}

          <div className="pp-header-card">
            <div className="pp-header-left">
              <div className="pp-header-photo-wrap">
                <button
                  type="button"
                  className="pp-header-photo-btn"
                  onClick={handlePhotoEdit}
                  aria-label="Update profile photo"
                >
                  {provider.profilePhotoUrl && !photoLoadFailed ? (
                    <img
                      src={provider.profilePhotoUrl}
                      alt=""
                      className="pp-header-photo"
                      onError={() => setPhotoLoadFailed(true)}
                    />
                  ) : (
                    <div
                      className="pp-header-photo pp-header-photo-fallback"
                      aria-hidden="true"
                    >
                      {photoInitials}
                    </div>
                  )}
                </button>
                <button
                  type="button"
                  className="pp-photo-edit"
                  onClick={handlePhotoEdit}
                  aria-label="Edit photo"
                >
                  <FaEdit size={11} />
                </button>
              </div>
              <div className="pp-header-meta">
                <div className="pp-header-name-row">
                  <h2 className="pp-header-name">{displayName}</h2>
                  <StatusPill status={displayStatus} />
                </div>
                <p className="pp-header-subtitle">
                  {providerType} · Provider ID: {providerDisplayId}
                </p>
              </div>
            </div>

            {canManageStatus && (
              <div className="pp-header-actions">
                {displayStatus === "Inactive" ? (
                  <button
                    type="button"
                    className="pp-activate-btn"
                    onClick={handleActivate}
                    disabled={isDeactivating}
                  >
                    <FaUserPlus size={13} />
                    {isDeactivating ? "Activating..." : "Activate Provider"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="pp-deactivate-btn"
                    onClick={handleDeactivate}
                    disabled={isDeactivating}
                  >
                    <FaUserMinus size={13} />
                    Deactivate Provider
                  </button>
                )}
              </div>
            )}
          </div>

          <nav className="pp-tabs" role="tablist" aria-label="Provider profile sections">
            {PROFILE_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`pp-tab-${tab.id}`}
                  aria-selected={isActive}
                  aria-controls={`pp-panel-${tab.id}`}
                  tabIndex={isActive ? 0 : -1}
                  className={`pp-tab ${isActive ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    const params = new URLSearchParams(location.search);
                    params.set("tab", tab.id);
                    navigate(
                      { pathname: location.pathname, search: params.toString() },
                      { replace: true },
                    );
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>

          <div className="pp-main">
            {activeTab === "profile" && (
              <div
                role="tabpanel"
                id="pp-panel-profile"
                aria-labelledby="pp-tab-profile"
              >
                <ProviderProfileTab
                  provider={provider}
                  isTherapist={isTherapist}
                  isOwnProfile={isOwnProfile}
                  getProviderId={getProviderId}
                  onProviderUpdated={(next) => {
                    setProvider(next);
                    setUpdatedProvider(next);
                  }}
                />
              </div>
            )}

            {activeTab === "credentials" && (
              <div
                role="tabpanel"
                id="pp-panel-credentials"
                aria-labelledby="pp-tab-credentials"
              >
                {isTherapist ? (
                  <ProviderCredentialsTab
                    provider={provider}
                    getProviderId={getProviderId}
                    fetchedStates={fetchedStates}
                    onProviderUpdated={(next) => {
                      setProvider(next);
                      setUpdatedProvider(next);
                    }}
                  />
                ) : (
                  <ProfileSection title="Licenses & Credentials">
                    <p className="pp-muted">
                      Licenses & credentials apply to Therapist providers only.
                    </p>
                  </ProfileSection>
                )}
              </div>
            )}

            {activeTab === "payments" && (
              <div
                role="tabpanel"
                id="pp-panel-payments"
                aria-labelledby="pp-tab-payments"
              >
                <ProviderPaymentsTab
                  provider={provider}
                  getProviderId={getProviderId}
                  fetchedStates={fetchedStates}
                  readOnlyInsurance={isOwnProfile}
                  hideAccessCode={isOwnProfile}
                  onProviderUpdated={(next) => {
                    setProvider(next);
                    setUpdatedProvider(next);
                  }}
                />
              </div>
            )}

            {activeTab === "availability" && (
              <div
                role="tabpanel"
                id="pp-panel-availability"
                aria-labelledby="pp-tab-availability"
              >
                <ProviderAvailabilityTab getProviderId={getProviderId} />
              </div>
            )}

            {activeTab === "settings" && (
              <div
                role="tabpanel"
                id="pp-panel-settings"
                aria-labelledby="pp-tab-settings"
              >
                <ProviderSettingsTab
                  provider={provider}
                  getProviderId={getProviderId}
                  isAdminView={!isOwnProfile}
                  onAdminChangeEmail={() => {
                    setActiveTab("profile");
                    const params = new URLSearchParams(location.search);
                    params.set("tab", "profile");
                    navigate(
                      { pathname: location.pathname, search: params.toString() },
                      { replace: true },
                    );
                  }}
                  onProviderUpdated={(next) => {
                    setProvider(next);
                    setUpdatedProvider(next);
                  }}
                />

              </div>
            )}

            {editingPhoto && (
              <div className="modal-overlay">
                <div className="modal-container">
                  <div className="modal-header">
                    <h3>Update Profile Photo</h3>
                    <button
                      type="button"
                      className="close-button"
                      onClick={handlePhotoCancel}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="modal-content">
                    <StepProfilePhoto
                      data={{
                        profilePhoto: tempPhoto,
                        profilePhotoUrl: provider.profilePhotoUrl,
                      }}
                      onChange={(field, value) => setTempPhoto(value)}
                      onNext={() => handlePhotoSave(tempPhoto)}
                      onBack={handlePhotoCancel}
                      loading={isSaving}
                    />
                  </div>
                </div>
              </div>
            )}

            {showModal && !isOwnProfile && (
              <div className="modal-overlay pp-confirm-overlay">
                <div
                  className="modal-container pp-confirm-modal pp-deactivate-payer-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="pp-deactivate-provider-title"
                >
                  <p
                    className="modal-text"
                    id="pp-deactivate-provider-title"
                  >
                    Are you sure you want to <br /> make the provider inactive?
                  </p>
                  <div className="pp-confirm-actions">
                    <button
                      type="button"
                      className="confirm-button"
                      onClick={handleConfirmDeactivate}
                      disabled={isDeactivating}
                    >
                      {isDeactivating ? "Deactivating..." : "Yes, deactivate"}
                    </button>
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={handleCloseModal}
                      disabled={isDeactivating}
                    >
                      No, keep active
                    </button>
                  </div>
                </div>
              </div>
            )}
            <ToastContainer />
          </div>
        </div>
      ) : (
        <ProviderProfileForm
          provider={updatedProvider}
          handleInputChange={handleInputChange}
        />
      )}
    </>
  );
};

export default ProviderDetails;

